import { Stack, Typography, useTheme } from "@mui/material";
import { CircularLoader } from "components";
import { type ReactNode, useEffect, useState } from "react";
import type { MspNotificationRole } from "shared";
import { NotificationSettingsChip } from "./NotificationSettingsChip";
import { RESOURCE_NOTIFY_METHODS, USER_NOTIFY_METHODS } from "./types";
import { useFormContext } from "react-hook-form";
import { NotificationSoundChip } from "./NotificationSoundChip";
import {
  useUpdateMspResourceNotificationSettings,
  useUpdateMspUserNotificationSettings,
} from "controller/hooks/control-center/notifications";
import toast from "react-hot-toast";
import { useUpdateCustomerNotificationsSettings } from "controller";
import { useParams } from "react-router-dom";
import { ToggleButton } from "components/toggle";

export const NotificationSettingsItem = ({
  initialSettings,
  serviceType,
  notificationType,
  isFirstItem = false,
  isLastItem = false,
  roleTypes,
  isRoleTypesDataLoading,
  searchQuery,
  setSearchQuery,
  isDisabled = false,
  titleAdornment,
  endAction,
}: {
  initialSettings: { notification_status?: boolean } | undefined;
  serviceType: string;
  notificationType: {
    key: string;
    title: string;
    isChatBased?: boolean;
  };
  isFirstItem?: boolean;
  isLastItem?: boolean;
  roleTypes: MspNotificationRole[];
  isRoleTypesDataLoading: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  isDisabled?: boolean;
  /** Rendered after the title, e.g. a tag. */
  titleAdornment?: ReactNode;
  /** Rendered at the row's right end, only while the notification is on and the row is enabled. */
  endAction?: ReactNode;
}) => {
  const { id: customerId } = useParams();
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(
    initialSettings?.notification_status || false,
  );
  const [isNotificationOn, setIsNotificationOn] = useState(
    initialSettings?.notification_status || false,
  );

  useEffect(() => {
    setIsExpanded(initialSettings?.notification_status || false);
    setIsNotificationOn(initialSettings?.notification_status || false);
  }, [initialSettings]);

  const personnelType = serviceType?.split(".")?.[0];
  const RESOURCE_NOTIFY_METHODS = [
    "web_app",
    "email",
    "resource_mobile_app",
    "notification_sound",
  ];
  const USER_NOTIFY_METHODS = [
    "agent_app_desktop",
    "email",
    "agent_mobile_app",
    "teams_app",
    "notification_sound",
  ];
  const NOTIFY_METHODS =
    personnelType === "users" || personnelType === "customers"
      ? (USER_NOTIFY_METHODS as USER_NOTIFY_METHODS[])
      : (RESOURCE_NOTIFY_METHODS as RESOURCE_NOTIFY_METHODS[]);

  const getBorderBottom = () => {
    if (isLastItem) {
      return "none";
    } else {
      return `1px solid ${theme.palette.neutral["015"]}`;
    }
  };
  const borderBottom = getBorderBottom();
  const { control } = useFormContext();

  const {
    mutateAsync: updateMspResourceNotificationSettings,
    isLoading: isUpdateMspResourceNotificationSettingsLoading,
  } = useUpdateMspResourceNotificationSettings();
  const {
    mutateAsync: updateMspUserNotificationSettings,
    isLoading: isUpdateMspUserNotificationSettingsLoading,
  } = useUpdateMspUserNotificationSettings();
  const {
    mutateAsync: updateCustomerNotificationsSettings,
    isLoading: isUpdateCustomerNotificationsSettingsLoading,
  } = useUpdateCustomerNotificationsSettings();

  const onHeaderClick = () => {
    if (
      !isUpdateMspResourceNotificationSettingsLoading &&
      !isUpdateMspUserNotificationSettingsLoading &&
      !isUpdateCustomerNotificationsSettingsLoading
    ) {
      setIsExpanded((prev: boolean) => !prev);
      const finalData = {
        notification_status: true,
        notification_sound: true,
        notify_methods: [
          ...(personnelType === "users" || personnelType === "customers"
            ? [
                {
                  notify_through: "agent_app_desktop",
                  roles: [
                    {
                      role_id: "ce82a917-c481-4b4f-a381-c7209addc1d1",
                      role_name: "manager_1",
                    },
                    {
                      role_id: "639846fe-28cc-441c-8d5a-f2dfd3852dc2",
                      role_name: "customer_user",
                    },
                  ],
                },
              ]
            : [
                {
                  notify_through: "web_app",
                  roles: [
                    {
                      role_id: "fcbd88f6-dec3-404b-8cd0-425a845d0766",
                      role_name: "all",
                    },
                  ],
                },
              ]),
        ],
      };
      if (!isNotificationOn) {
        (async () => {
          try {
            setIsNotificationOn(true);
            await (personnelType === "users"
              ? updateMspUserNotificationSettings({
                  data: finalData,
                  notificationType: notificationType?.key,
                })
              : personnelType === "customers"
                ? updateCustomerNotificationsSettings({
                    data: finalData,
                    customerId: customerId || "",
                    notificationType: notificationType?.key,
                  })
                : updateMspResourceNotificationSettings({
                    data: finalData,
                    notificationType: notificationType?.key,
                  }));
            toast.success("Updated notification settings successfully");
          } catch {
            toast.error("Failed to update notification settings");
            setIsExpanded(false);
            setIsNotificationOn(false);
          }
        })();
      }
    }
  };

  const turnOffNotification = async () => {
    try {
      setIsNotificationOn(false);
      const finalData = {
        notification_status: false,
        notification_sound: false,
        notify_methods: [],
      };
      await (personnelType === "users"
        ? updateMspUserNotificationSettings({
            data: finalData,
            notificationType: notificationType?.key,
          })
        : personnelType === "customers"
          ? updateCustomerNotificationsSettings({
              data: finalData,
              customerId: customerId || "",
              notificationType: notificationType?.key,
            })
          : updateMspResourceNotificationSettings({
              data: finalData,
              notificationType: notificationType?.key,
            }));
      toast.success("Turned off notification successfully");
    } catch {
      toast.error("Failed to turn off notification");
      setIsNotificationOn(true);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap="16px"
      p="16px"
      bgcolor={theme.palette.neutral.main}
      borderBottom={borderBottom}
      borderRadius={`${isFirstItem ? "8px 8px" : "0px 0px"} ${
        isLastItem ? "8px 8px" : "0px 0px"
      }`}
    >
      <ToggleButton
        isDisabled={isDisabled}
        isChecked={isNotificationOn}
        onChange={(value: boolean) => {
          if (isNotificationOn) {
            // Collapse at once — the chips used to collapse via the header
            // click this toggle sat inside before the row redesign.
            setIsExpanded(false);
            turnOffNotification();
          } else {
            onHeaderClick();
          }
        }}
      />
      <Stack flex="1 1 0" minWidth={0} gap="16px">
        <Stack
          direction="row"
          alignItems="center"
          gap="8px"
          minHeight="24px"
          sx={{ cursor: "pointer" }}
          onClick={onHeaderClick}
        >
          <Typography
            variant="subtitle1"
            color={
              isDisabled ? theme.palette.lightBg.low : theme.palette.lightBg.high
            }
          >
            {notificationType.title}
          </Typography>
          {titleAdornment}
          {(isUpdateMspResourceNotificationSettingsLoading ||
            isUpdateMspUserNotificationSettingsLoading ||
            isUpdateCustomerNotificationsSettingsLoading) && (
            <CircularLoader size="14px" color="primary" />
          )}
        </Stack>
        {isExpanded && (
          <Stack
            direction="row"
            alignItems={"center"}
            gap="8px"
            flexWrap={"wrap"}
          >
            {NOTIFY_METHODS?.map((notifyMethod) => {
              return notifyMethod === "notification_sound" ? (
                personnelType === "users" || personnelType === "customers" ? (
                  <></>
                ) : (
                  <NotificationSoundChip
                    name={`${serviceType}.${notificationType.key}.${notifyMethod}`}
                    control={control}
                    key={notifyMethod}
                    notifyMethod={notifyMethod}
                  />
                )
              ) : (
                <NotificationSettingsChip
                  name={`${serviceType}.${notificationType.key}.${notifyMethod}`}
                  control={control}
                  key={notifyMethod}
                  notifyMethod={notifyMethod}
                  roleTypes={roleTypes}
                  isRoleTypesDataLoading={isRoleTypesDataLoading}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  isChatBased={notificationType.isChatBased ?? false}
                  notificationType={
                    personnelType === "users" || personnelType === "customers"
                      ? "user"
                      : "resource"
                  }
                  isDisabled={isDisabled}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
      {isNotificationOn && !isDisabled && endAction}
    </Stack>
  );
};
